import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Plug, LayoutDashboard, BarChart3, MessageSquare,
  Camera, Sparkles, BookOpen, Wand2,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { apiClient } from "@/lib/api"

export interface ChecklistItem {
  key: string
  label: string
  icon: LucideIcon
  linkTo: string
  completed: boolean
  completedAt: string | null
}

const CHECKLIST_ITEMS: Array<{
  key: string
  label: string
  icon: LucideIcon
  linkTo: string
}> = [
  { key: "property_connected", label: "Connect your property", icon: Plug, linkTo: "/onboarding" },
  { key: "overview_viewed", label: "View your dashboard", icon: LayoutDashboard, linkTo: "" },
  { key: "performance_viewed", label: "Check your revenue", icon: BarChart3, linkTo: "performance" },
  { key: "reviews_viewed", label: "See your reputation", icon: MessageSquare, linkTo: "reviews" },
  { key: "listing_audited", label: "Audit your listing & photos", icon: Camera, linkTo: "listings" },
  { key: "ai_feature_used", label: "Experience AI insights", icon: Sparkles, linkTo: "" },
  { key: "guidebook_viewed", label: "Preview your guidebook", icon: BookOpen, linkTo: "guidebook" },
  { key: "first_ai_welcome_rendered", label: "See your AI welcome go live", icon: Wand2, linkTo: "guidebook" },
]

export function useOnboardingChecklist(propertyId: string | undefined) {
  const [events, setEvents] = useState<Map<string, string>>(new Map())
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!propertyId) return
    setIsLoading(true)
    apiClient.getActivationEvents(propertyId)
      .then(res => {
        if (res.ok) {
          const map = new Map<string, string>()
          for (const e of res.events) {
            map.set(e.event_key, e.completed_at)
          }
          setEvents(map)
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [propertyId])

  const items: ChecklistItem[] = useMemo(() =>
    CHECKLIST_ITEMS.map(item => ({
      ...item,
      completed: events.has(item.key),
      completedAt: events.get(item.key) || null,
    })),
    [events]
  )

  const completedCount = useMemo(() => items.filter(i => i.completed).length, [items])
  const isFullyComplete = completedCount === CHECKLIST_ITEMS.length

  const recordEvent = useCallback((key: string) => {
    if (!propertyId || events.has(key)) return
    // Optimistic update
    setEvents(prev => new Map(prev).set(key, new Date().toISOString()))
    apiClient.recordActivationEvent(propertyId, key).catch(() => {
      // Revert on failure
      setEvents(prev => {
        const next = new Map(prev)
        next.delete(key)
        return next
      })
    })
  }, [propertyId, events])

  return {
    items,
    completedCount,
    totalCount: CHECKLIST_ITEMS.length,
    isFullyComplete,
    isLoading,
    recordEvent,
  }
}
